import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../../../components/ui/Icon';
import { MenuAnchor } from '../../../components/ui/MenuAnchor';
import { asset } from '../../../lib/asset';
import { brand, nav } from '../../../content/site';
import type { MenuLink, MenuPanel } from '../../../content/site';
import { useMenu, useSiteInfo } from '../../../hooks/useCms';

/**
 * Mega menü mekaniği:
 *  - navigasyon çubuğu hover/açık durumda buzlu bir karta dönüşür; menü
 *    açılınca alt köşeleri düzleşip panelle tek gövde olur
 *  - panel yüksekliği menüler arasında morph eder (300ms, ease-nav)
 *  - içerik çapraz geçer: giren ∓%20'den kayarak gelir, çıkan ±%20'ye gider
 *    (opaklık 250ms / dönüşüm 500ms, cubic-bezier(.4,0,.2,1))
 *  - sayfanın kalanı blur(5px) + koyu perde ile geri çekilir
 */

const NAV_H = 76;
const CLOSE_DELAY = 120;

/** Panelin sütun sayısı; sınıflar statik kalmalı. */
const GRID_COLS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
};

function SoonBadge() {
  return (
    <span className="ml-2 inline-flex rounded-full bg-white/[0.07] px-2 py-0.5 align-middle text-[11px] font-bold text-ink-soft">
      Çok yakında
    </span>
  );
}

/** Bağlantının rozeti: "Çok yakında" (CMS `isSoon`) öncelikli, yoksa admin'de yazılan kısa rozet (`badge`, ör. Z'eki "Beta"). */
function LinkBadge({ link }: { link: MenuLink }) {
  if (link.soon) return <SoonBadge />;
  if (!link.badge) return null;
  return (
    <span className="ml-2 inline-flex rounded-full border border-[#fbbf24]/30 bg-[#fbbf24]/10 px-2 py-0.5 align-middle text-[11px] font-bold text-[#fbbf24]">
      {link.badge}
    </span>
  );
}

function RichLink({ link }: { link: MenuLink }) {
  return (
    <MenuAnchor href={link.href} className="group flex gap-3 rounded-[8px] no-underline outline-offset-4">
      {link.icon && (
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-brand-300/[0.12] text-action-text">
          <Icon name={link.icon} size={17} />
        </span>
      )}
      <span className="min-w-0">
        <span className="block text-[14px] font-bold text-action-text transition-colors duration-nav ease-nav group-hover:text-white group-focus-visible:text-white">
          {link.label}
          <LinkBadge link={link} />
          {/* Ok rengini metinden (currentColor) alır; hover'da soldan kayarak belirir.
              Rozetten sonra durur: gizliyken de yer kapladığı için rozeti metinden uzaklaştırmasın. */}
          <Icon
            name="arrowRight"
            size={14}
            strokeWidth={2}
            className="ml-1 inline-block -translate-x-1 align-[-2px] opacity-0 transition-[opacity,transform] duration-150 ease-navContent group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100 motion-reduce:transition-none"
          />
        </span>
        {link.desc && <span className="mt-0.5 block text-[13px] leading-[1.45] text-ink-soft">{link.desc}</span>}
      </span>
    </MenuAnchor>
  );
}

type Edge = 'left' | 'right';

/** Çizginin `clip-path` hâlleri: tam görünür ya da bir kenarda sıfır genişliğe toplanmış. */
const CLIP_SHOWN = 'inset(0 0% 0 0%)';
const CLIP_HIDDEN: Record<Edge, string> = { left: 'inset(0 100% 0 0%)', right: 'inset(0 0% 0 100%)' };
// Hızlı başlayıp uzun ve yumuşak biten eğri: çizgi imlecin hareketine gecikmeden tepki verir, yarıda yön
// değişince duraksamaz (ease-in-out'un yavaş başlangıcı çizgiyi imlecin gerisinde bırakıyordu).
const LINE_MS = 360;
const LINE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Aktif sütun çizgisi: imleci takip eder, hep bir KENARDAN açılır.
 *  - Her başlığın alt kenarlığında sütun genişliğinde bir çizgi vardır; görünmezken bir kenarda sıfır
 *    genişliğe toplanmıştır. Sütunlar arasındaki boşlukta hiçbir çizgi görünmez.
 *  - Hangi kenar: imlecin yatay hareket yönü. Sağa giderken girilen sütunda çizgi soldan açılır, çıkılan
 *    sütunda sağa doğru toplanır (sola giderken tersi): çizgi imleçle aynı yöne akar. Hareket neredeyse
 *    dikeyse (menü düğmesinden aşağı inmek) imlece yakın kenar seçilir; çizgi ortadan açılmaz.
 *  - Animasyon iki ucu `clip-path: inset()` ile oynatır ve HER ZAMAN çizginin o anki hâlinden başlar
 *    (Web Animations API): yarıda yön değişse de sıçrama olmaz. Yalnız tamamen gizliyken başlangıç
 *    kenarı anında değiştirilir (sıfır genişlikte olduğu için görünmez).
 *  - Projede animasyon kütüphanesi yok; `prefers-reduced-motion`'da anında açılır/kapanır.
 *  - Klavyede yön odak sırasından. Panel kapanınca sıfırlanır; Ürünler ve Çözümler panelleri ayrı
 *    bileşen olduğu için çizgileri birbirinden bağımsızdır.
 */
function PanelContent({ panel, open }: { panel: MenuPanel; open: boolean }) {
  const [activeColumn, setActiveColumn] = useState<number | null>(null);
  const [wasOpen, setWasOpen] = useState(open);
  const lineRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const hidden = useRef<boolean[]>([]);
  const velocity = useRef({ x: 0, y: 0 });
  const lastPointer = useRef<{ x: number; y: number } | null>(null);
  const lastColumn = useRef<number | null>(null);

  // Panel kapanınca aktif sütun sıfırlanır; efekt yerine render sırasında, bir kare bile eski sütun kalmasın.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) setActiveColumn(null);
  }

  // Panel açıkken imlecin yatay/dikey hızı izlenir (yumuşatılmış; tek olaydaki titreme yönü bozmasın).
  // Kapanınca çizgiler animasyonsuz gizlenir: bir sonraki açılış temiz başlar.
  useEffect(() => {
    if (!open) {
      lineRefs.current.forEach((el, c) => {
        if (!el) return;
        el.getAnimations().forEach((a) => a.cancel());
        el.style.clipPath = CLIP_HIDDEN.left;
        hidden.current[c] = true;
      });
      lastPointer.current = null;
      velocity.current = { x: 0, y: 0 };
      return;
    }
    const onMove = (e: PointerEvent) => {
      const last = lastPointer.current;
      if (last) {
        velocity.current = {
          x: velocity.current.x * 0.5 + (e.clientX - last.x) * 0.5,
          y: velocity.current.y * 0.5 + (e.clientY - last.y) * 0.5,
        };
      }
      lastPointer.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [open]);

  /** Çizgiyi o anki hâlinden hedefe oynatır; gizliyse `from` kenarından başlatır. */
  const play = (c: number, to: string, from?: Edge) => {
    const el = lineRefs.current[c];
    if (!el) return;
    const wasHidden = hidden.current[c] !== false;
    // Süren animasyonun o anki değeri; iptalden ÖNCE okunur ki yeni animasyon kaldığı yerden sürsün.
    const current = getComputedStyle(el).clipPath;
    el.getAnimations().forEach((a) => a.cancel());
    const start = wasHidden && from ? CLIP_HIDDEN[from] : current;
    el.style.clipPath = to;
    hidden.current[c] = false;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const done = () => {
      if (to !== CLIP_SHOWN) hidden.current[c] = true;
    };
    if (reduce || start === to) return done();
    el.animate([{ clipPath: start }, { clipPath: to }], { duration: LINE_MS, easing: LINE_EASE }).onfinish = done;
  };

  /** İmlecin girişte geldiği / çıkışta gittiği kenar. */
  const edgeOf = (e: MouseEvent<HTMLElement>, entering: boolean): Edge => {
    const { x, y } = velocity.current;
    if (Math.abs(x) >= 0.5 && Math.abs(x) >= Math.abs(y) * 0.35) {
      const movingRight = x > 0;
      return entering === movingRight ? 'left' : 'right';
    }
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientX - r.left < r.width / 2 ? 'left' : 'right';
  };

  const enter = (c: number, from: Edge) => {
    play(c, CLIP_SHOWN, from);
    setActiveColumn(c);
    lastColumn.current = c;
  };
  const leave = (c: number, to: Edge) => {
    play(c, CLIP_HIDDEN[to]);
    setActiveColumn((current) => (current === c ? null : current));
  };

  return (
    <div className="flex">
      <div className={`grid flex-1 gap-x-10 gap-y-8 px-10 py-8 ${GRID_COLS[panel.columns.length] ?? GRID_COLS[2]}`}>
        {panel.columns.map((col, c) => (
          <div
            key={`${c}-${col.title}`}
            onMouseEnter={(e) => enter(c, edgeOf(e, true))}
            onMouseLeave={(e) => leave(c, edgeOf(e, false))}
            // Klavye: sağdaki sütuna geçince soldan, soldakine geçince sağdan açılır (focus kabarcıklanır).
            onFocus={() => {
              if (activeColumn === c) return;
              enter(c, lastColumn.current !== null && lastColumn.current > c ? 'right' : 'left');
            }}
            onBlur={(e) => {
              // Aynı sütunda bağlantıdan bağlantıya geçmek çizgiyi kapatmaz.
              const next = e.relatedTarget as Node | null;
              if (next && e.currentTarget.contains(next)) return;
              // Odak ileri (sağa) gidiyorsa çizgi sağa, geri gidiyorsa sola toplanır.
              const forward = next !== null && (e.currentTarget.compareDocumentPosition(next) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
              leave(c, forward ? 'right' : 'left');
            }}
          >
            <h3 className="relative mb-4 border-b border-white/[0.07] pb-3 text-[13px] font-semibold text-ink-subdued">
              <span
                className={`transition-colors duration-navSlow ease-navContent motion-reduce:transition-none ${
                  activeColumn === c ? 'text-brand-300' : ''
                }`}
              >
                {col.title}
              </span>
              {/* 1.5px, kenarlıkla aynı alt çizgide (kenarlığın üstüne biner). Başlangıçta gizli (sol kenarda).
                  Gradient tüm sütun genişliğine yayılır; clip-path yalnız görünen kısmı açtığı için açılırken
                  renkler sıkışmaz, yerinde durur. */}
              <span
                ref={(el) => {
                  lineRefs.current[c] = el;
                }}
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 -bottom-px h-[1.5px] rounded-full bg-gradient-accent [clip-path:inset(0_100%_0_0%)]"
              />
            </h3>
            <ul className="flex flex-col gap-[18px]">
              {col.links.map((link, l) => (
                <li key={`${l}-${link.href}`}>
                  <RichLink link={link} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {panel.aside && (
        <aside className="w-[272px] shrink-0 border-l border-white/[0.07] bg-brand-300/[0.05] p-8">
          <h3 className="mb-3 font-display text-[17px] font-normal leading-[1.3] text-white">{panel.aside.title}</h3>
          {panel.aside.desc && <p className="mb-5 text-[13px] leading-[1.55] text-ink-soft">{panel.aside.desc}</p>}
          {panel.aside.button ? (
            <MenuAnchor
              href={panel.aside.href}
              className="zh-btn inline-flex h-11 items-center gap-2 rounded-[12px] bg-[linear-gradient(135deg,#1a3d6f_0%,#45c0ee_100%)] px-4 text-[14px] font-bold text-white no-underline"
            >
              {panel.aside.cta}
            </MenuAnchor>
          ) : (
            <MenuAnchor
              href={panel.aside.href}
              className="inline-flex items-center gap-1 text-[14px] font-bold text-action-text no-underline hover:text-action-textHover"
            >
              {panel.aside.cta}
              <Icon name="chevronRight" size={16} strokeWidth={2} />
            </MenuAnchor>
          )}
        </aside>
      )}
    </div>
  );
}

export default function Navbar() {
  const site = useSiteInfo();
  // Menü admin panelinden gelir; giriş ve demo düğmeleri koddadır.
  const { items } = useMenu();
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const [prev, setPrev] = useState<number | null>(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<number | null>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // Tetikleyiciden panele geçerken imleç kısa süre boşlukta kalıyor; kapanış
  // küçük bir gecikmeyle planlanır ki menü elin altından kaçmasın.
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setActive(null), CLOSE_DELAY);
  }, []);

  const open = useCallback((index: number) => {
    cancelClose();
    setActive((current) => {
      if (current !== index) setPrev(current);
      return index;
    });
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active]);

  // Panel yüksekliği ölçülüp kapsayıcıya yazılır; menüler arası geçişte bu
  // değer değiştiği için yükseklik animasyonu kendiliğinden morph olur.
  useLayoutEffect(() => {
    if (active === null) {
      setPanelHeight(0);
      return;
    }
    const el = panelRefs.current[active];
    if (!el) return;
    const measure = () => setPanelHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);

  useEffect(() => cancelClose, []);

  const isOpen = active !== null;
  // Bar başlangıçta saydam; buzlu kart yalnız kaydırma, hover, açık menü
  // ya da açık mobil menüde belirir.
  const cardVisible = scrolled || hovered || isOpen || mobileOpen;

  // Giriş/çıkış yönü menünün çubuktaki sırasına göre belirlenir.
  const offsetOf = (index: number) => {
    if (index === active) return '0%';
    const reference = active ?? prev ?? 0;
    return index < reference ? '-20%' : '20%';
  };

  return (
    <>
      <div
        aria-hidden="true"
        className={`fixed inset-x-0 bottom-0 z-40 backdrop-blur-[5px] transition-opacity duration-navSlow ease-nav motion-reduce:transition-none ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        style={{
          top: NAV_H - 10,
          background: 'linear-gradient(rgba(7,12,24,0), rgba(7,12,24,0.75))',
        }}
        onMouseEnter={scheduleClose}
      />

      <header className="fixed inset-x-0 top-0 z-50">
        <div className="mx-auto w-full max-w-[1216px] px-2">
          <div
            className="relative"
            onMouseLeave={() => {
              setHovered(false);
              scheduleClose();
            }}
            onMouseEnter={() => {
              setHovered(true);
              cancelClose();
            }}
          >
            {/* Buzlu kart: menü açıkken alt köşeleri düzleşip panelle birleşir. */}
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute inset-x-0 inset-y-1.5 rounded-[16px] border border-brand-300/[0.14] bg-[rgba(15,23,41,0.82)] shadow-navbar backdrop-blur-[18px] transition-[opacity,border-radius] duration-nav ease-nav motion-reduce:transition-none ${
                cardVisible ? 'opacity-100' : 'opacity-0'
              } ${isOpen || mobileOpen ? 'rounded-b-none border-b-transparent' : ''}`}
            />

            {/* İç boşluk, logo sayfa içeriğiyle (max-w-content + px-4/sm:px-6) aynı çizgiden başlasın diye:
                kart 1216px'ten dar ekranda kenardan 8px içeride durur, iç boşluk 16px; geniş ekranda 24px.
                Masaüstünde logo, menü ve sağ düğmeler arasında eşit boşluk (justify-between): sağ grup logodan geniş
                olduğu için kartın tam ortasına konan menü sağa yapışık görünüyordu. */}
            <div
              className="relative flex items-center justify-between px-4 min-[1216px]:px-6"
              style={{ height: NAV_H }}
            >
              {/* Logo ana sayfaya gider; router ile (tam yükleme yok, menüyle aynı). */}
              <Link to="/" className="flex items-center" aria-label={brand.name}>
                {/* Kenar boşluğu kırpılmış logo: sayfa içeriğiyle aynı çizgiden başlar (görünen yükseklik 26px → 31px).
                    Eski "logo canlanması" (zh-logo-in) kaldırıldı: açılış animasyonu index.html'deki splash (2 Kasım). */}
                <img src={asset('assets/z-hesap-logo-dark-tight.svg')} width={195} height={53} alt={brand.name} className="h-[31px] w-auto" />
              </Link>

              <nav aria-label="Ana menü" className="hidden items-center gap-0.5 lg:flex">
                {items.map((item, i) =>
                  item.panel ? (
                    <button
                      key={`${i}-${item.label}`}
                      type="button"
                      aria-expanded={active === i}
                      onMouseEnter={() => open(i)}
                      onFocus={() => open(i)}
                      onClick={() => (active === i ? setActive(null) : open(i))}
                      className={`flex h-11 items-center gap-1 whitespace-nowrap rounded-[10px] px-2 text-[14px] font-bold transition-colors duration-nav ease-nav xl:px-3 ${
                        active === i ? 'text-white' : 'text-ink-soft hover:text-white'
                      }`}
                    >
                      {item.label}
                      <Icon
                        name="chevronDown"
                        size={16}
                        strokeWidth={2}
                        className={`transition-transform duration-nav ease-nav motion-reduce:transition-none ${
                          active === i ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  ) : (
                    <MenuAnchor
                      key={`${i}-${item.label}`}
                      href={item.href}
                      onMouseEnter={scheduleClose}
                      className="flex h-11 items-center whitespace-nowrap rounded-[10px] px-2 text-[14px] font-bold text-ink-soft no-underline transition-colors duration-nav ease-nav hover:text-white xl:px-3"
                    >
                      {item.label}
                    </MenuAnchor>
                  )
                )}
              </nav>

              <div className="hidden shrink-0 items-center gap-1.5 lg:flex" onMouseEnter={scheduleClose}>
                <a
                  href={site.appUrl}
                  className="flex h-11 items-center rounded-[10px] px-3 text-[14px] font-bold text-ink-soft no-underline transition-colors duration-nav ease-nav hover:text-white"
                >
                  {nav.login.label}
                </a>
                <Link
                  to={nav.cta.href}
                  className="zh-btn inline-flex h-10 items-center gap-1.5 rounded-[11px] bg-[linear-gradient(135deg,#2563eb,#3b82f6)] px-4 text-[13px] font-bold text-white no-underline shadow-[0_8px_24px_-8px_rgba(59,130,246,0.7)]"
                >
                  {nav.cta.label}
                  <Icon name="arrowRight" size={15} strokeWidth={2} />
                </Link>
              </div>

              <button
                type="button"
                onClick={() => setMobileOpen((v) => !v)}
                aria-expanded={mobileOpen}
                aria-label="Menü"
                className="flex h-11 w-11 items-center justify-center rounded-[10px] text-white lg:hidden"
              >
                <Icon name={mobileOpen ? 'close' : 'menu'} size={22} strokeWidth={2} />
              </button>
            </div>

            {/* Panel. Yükseklik morph eder; içerik `overflow: clip` ile kırpılır. */}
            <div
              className={`absolute inset-x-0 top-full hidden overflow-clip rounded-b-[16px] border border-brand-300/[0.14] border-t-white/[0.06] bg-[rgba(13,21,38,0.96)] backdrop-blur-[18px] transition-[height,opacity] duration-navSlow ease-nav motion-reduce:transition-none lg:block ${
                isOpen ? 'opacity-100 shadow-menu' : 'pointer-events-none opacity-0'
              }`}
              style={{ height: panelHeight, marginTop: -6 }}
            >
              <div className="relative">
                {items.map((item, i) =>
                  item.panel ? (
                    <div
                      key={`${i}-${item.label}`}
                      ref={(el) => {
                        panelRefs.current[i] = el;
                      }}
                      aria-hidden={active !== i}
                      inert={active !== i}
                      className="absolute inset-x-0 top-0 transition-[opacity,transform] ease-navContent motion-reduce:transition-none"
                      style={{
                        opacity: active === i ? 1 : 0,
                        transform: `translateX(${offsetOf(i)})`,
                        transitionDuration: '250ms, 500ms',
                        pointerEvents: active === i ? 'auto' : 'none',
                      }}
                    >
                      <PanelContent panel={item.panel} open={active === i} />
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="mx-2 -mt-1.5 max-h-[calc(100vh-76px)] overflow-y-auto rounded-b-[16px] border border-t-0 border-brand-300/[0.14] bg-[rgba(13,21,38,0.98)] px-4 pb-6 shadow-menu backdrop-blur-[18px] lg:hidden">
            <nav aria-label="Mobil menü" className="divide-y divide-white/[0.07]">
              {items.map((item, i) =>
                item.panel ? (
                  <div key={`${i}-${item.label}`}>
                    <button
                      type="button"
                      aria-expanded={mobileSection === i}
                      onClick={() => setMobileSection((v) => (v === i ? null : i))}
                      className="flex min-h-[52px] w-full items-center justify-between text-[16px] font-bold text-white"
                    >
                      {item.label}
                      <Icon
                        name="chevronDown"
                        size={20}
                        strokeWidth={2}
                        className={`transition-transform duration-nav ease-nav ${mobileSection === i ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {/* Mobilde sütunlar alt alta iner ama başlıkları korunur. */}
                    {mobileSection === i && (
                      <div className="space-y-6 pb-5">
                        {item.panel.columns.map((col, c) => (
                          <div key={`${c}-${col.title}`}>
                            <h3 className="mb-3 text-[13px] font-semibold text-ink-subdued">{col.title}</h3>
                            <ul className="space-y-4">
                              {col.links.map((link, l) => (
                                <li key={`${l}-${link.href}`}>
                                  <MenuAnchor
                                    href={link.href}
                                    onClick={() => setMobileOpen(false)}
                                    className="block text-[14px] font-bold text-action-text no-underline"
                                  >
                                    {link.label}
                                    <LinkBadge link={link} />
                                  </MenuAnchor>
                                  {link.desc && (
                                    <span className="mt-0.5 block text-[13px] text-ink-soft">{link.desc}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <MenuAnchor
                    key={`${i}-${item.label}`}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex min-h-[52px] items-center text-[16px] font-bold text-white no-underline"
                  >
                    {item.label}
                  </MenuAnchor>
                )
              )}
            </nav>

            <div className="mt-6 flex flex-col gap-2">
              <Link
                to={nav.cta.href}
                onClick={() => setMobileOpen(false)}
                className="flex h-12 items-center justify-center gap-2 rounded-[12px] bg-[linear-gradient(135deg,#2563eb,#3b82f6)] text-[15px] font-bold text-white no-underline"
              >
                {nav.cta.label}
                <Icon name="arrowRight" size={16} strokeWidth={2} />
              </Link>
              <a
                href={site.appUrl}
                className="flex h-12 items-center justify-center rounded-[12px] border border-[rgba(120,150,210,0.3)] text-[15px] font-bold text-white no-underline"
              >
                {nav.login.label}
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
